import React from "react";
import {
  Button,
  Card,
  Flex,
  Tag,
  Typography,
  Divider,
  theme,
  Table,
  Collapse,
  Empty,
} from "antd";
import {
  BankOutlined,
  ShopOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  EditOutlined,
  AuditOutlined,
  SolutionOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";
import { useFetchCompany } from "../hooks/useFetchCompany";
import CompanyLogoUploader from "../components/CompanyLogoUploader";
import Loading from "../../shared/components/Loading";
import HeaderApp from "../../shared/components/HeaderApp";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

// ─── Helpers ──────────────────────────────────────────────────────────────────
import { formatDate } from "../../../helpers/formatDate";

const satStatusConfig: Record<
  string,
  { color: string; label: string; icon: React.ReactNode }
> = {
  ACTIVO: {
    color: "success",
    label: "ACTIVO Y VIGENTE",
    icon: <CheckCircleFilled />,
  },
  SUSPENDIDO: {
    color: "warning",
    label: "SUSPENDIDO",
    icon: <CloseCircleFilled />,
  },
  CANCELADO: {
    color: "error",
    label: "CANCELADO",
    icon: <CloseCircleFilled />,
  },
};

// ─── FieldItem ────────────────────────────────────────────────────────────────

const FieldItem = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  const { token } = theme.useToken();
  return (
    <Flex vertical gap={3}>
      <Text
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: token.colorTextSecondary,
        }}
      >
        {label}
      </Text>
      {children}
    </Flex>
  );
};

// ─── SectionCard ─────────────────────────────────────────────────────────────

const SectionCard = ({
  icon,
  title,
  iconBg,
  iconColor,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  iconBg: string;
  iconColor: string;
  children: React.ReactNode;
}) => {
  const { token } = theme.useToken();
  return (
    <Card
      style={{ borderRadius: token.borderRadiusLG, height: "100%" }}
      styles={{ body: { padding: 24 } }}
      hoverable
    >
      <Flex vertical gap={18}>
        <Flex align="center" gap={12}>
          <Flex
            align="center"
            justify="center"
            style={{
              width: 40,
              height: 40,
              borderRadius: token.borderRadiusLG,
              background: iconBg,
              color: iconColor,
              fontSize: 18,
              flexShrink: 0,
            }}
          >
            {icon}
          </Flex>
          <Title level={5} style={{ margin: 0, fontWeight: 800, fontSize: 16 }}>
            {title}
          </Title>
        </Flex>
        <Divider style={{ margin: 0 }} />
        <Flex vertical gap={16}>
          {children}
        </Flex>
      </Flex>
    </Card>
  );
};

const DetailsCompany = () => {
  const { data: company, isLoading } = useFetchCompany();
  const navigate = useNavigate();

  const { token } = theme.useToken();
  if (isLoading) {
    return (
      <>
        <Loading />
      </>
    );
  }

  if (!company) {
    return <>No data</>
  }
  const satStatus = satStatusConfig[company.status_sat?.toUpperCase()] ?? {
    color: "default",
    label: company.status_sat,
    icon: null,
  };

  // Table columns
  const actColumns = [
    {
      title: "Actividad Económica",
      dataIndex: "economic_activity",
      key: "description",
      render: (val: string) => <Text style={{ fontSize: 13 }}>{val}</Text>,
    },
    {
      title: "Porcentaje",
      dataIndex: "percentage",
      key: "percentage",
      width: 100,
      render: (val: number) =>
        val !== undefined ? <Tag color="blue">{val}%</Tag> : null,
    },
  ];

  const regimeColumns = [
    {
      title: "Régimen Fiscal",
      dataIndex: "regime",
      key: "description",
      render: (val: string) => <Text style={{ fontSize: 13 }}>{val}</Text>,
    },
    {
      title: "Clave",
      dataIndex: "code",
      key: "code",
      width: 90,
      render: (val: string) => (val ? <Tag>{val}</Tag> : null),
    },
  ];

  const obligationColumns = [
    {
      title: "Obligación",
      dataIndex: "obligation",
      key: "description",
      render: (val: string) => <Text style={{ fontSize: 13 }}>{val}</Text>,
    },
    {
      title: "Periodicidad",
      dataIndex: "periodicity",
      key: "periodicity",
      width: 120,
      render: (val: string) => (val ? <Tag color="purple">{val}</Tag> : null),
    },
  ];

  const collapseItems = [
    {
      key: "actividades",
      label: (
        <Flex align="center" gap={8}>
          <AuditOutlined style={{ color: token.colorPrimary }} />
          <Text strong>
            Actividades Económicas
            <Tag color="blue" style={{ marginLeft: 8, fontWeight: 700 }}>
              {company.company_economic_act?.length ?? 0}
            </Tag>
          </Text>
        </Flex>
      ),
      children:
        company.company_economic_act?.length > 0 ? (
          <Table
            dataSource={company.company_economic_act}
            columns={actColumns}
            rowKey={(_, i) => String(i)}
            pagination={false}
            size="small"
          />
        ) : (
          <Empty description="Sin actividades registradas" />
        ),
    },
    {
      key: "regimenes",
      label: (
        <Flex align="center" gap={8}>
          <SolutionOutlined style={{ color: token.colorSuccessText }} />
          <Text strong>
            Regímenes Fiscales
            <Tag color="green" style={{ marginLeft: 8, fontWeight: 700 }}>
              {company.regimes?.length ?? 0}
            </Tag>
          </Text>
        </Flex>
      ),
      children:
        company.regimes?.length > 0 ? (
          <Table
            dataSource={company.regimes}
            columns={regimeColumns}
            rowKey={(_, i) => String(i)}
            pagination={false}
            size="small"
          />
        ) : (
          <Empty description="Sin regímenes registrados" />
        ),
    },
    {
      key: "obligaciones",
      label: (
        <Flex align="center" gap={8}>
          <FileProtectOutlined style={{ color: token.colorWarning }} />
          <Text strong>
            Obligaciones Fiscales
            <Tag color="orange" style={{ marginLeft: 8, fontWeight: 700 }}>
              {company.obligations?.length ?? 0}
            </Tag>
          </Text>
        </Flex>
      ),
      children:
        company.obligations?.length > 0 ? (
          <Table
            dataSource={company.obligations}
            columns={obligationColumns}
            rowKey={(_, i) => String(i)}
            pagination={false}
            size="small"
          />
        ) : (
          <Empty description="Sin obligaciones registradas" />
        ),
    },
  ];

  return (
    <>
      <HeaderApp sectionName={"Detalles de la Empresa"} showBack />
      <Flex
        vertical
        gap={24}
        style={{ maxWidth: 860, margin: "0 auto", padding: "32px 16px 40px" }}
      >
        {/* ── Profile Header ── */}
        <Flex vertical align="center" gap={10} style={{ textAlign: "center" }}>
          <CompanyLogoUploader uuidLogo={company.uuid_logo} />

          <Title
            level={2}
            style={{
              margin: 0,
              fontWeight: 900,
              fontSize: 26,
              letterSpacing: "-0.02em",
              color: token.colorText,
            }}
          >
            {company.public_name_company || company.name_company}
          </Title>

          <Flex align="center" gap={10} wrap justify="center">
            <Tag
              color={satStatus.color}
              icon={satStatus.icon}
              style={{
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                borderRadius: 999,
                margin: 0,
              }}
            >
              {satStatus.label}
            </Tag>
          </Flex>
        </Flex>

        {/* ── Info Cards ── */}
        <Flex gap={20} wrap style={{ alignItems: "stretch" }}>
          {/* Datos Fiscales */}
          <div style={{ flex: "1 1 280px" }}>
            <SectionCard
              icon={<BankOutlined />}
              title="Datos Fiscales"
              iconBg={token.colorPrimaryBg}
              iconColor={token.colorPrimary}
            >
              <FieldItem label="RFC">
                <Text
                  strong
                  copyable
                  style={{
                    fontSize: 16,
                    fontWeight: 900,
                    color: token.colorText,
                  }}
                >
                  {company.rfc_company}
                </Text>
              </FieldItem>

              <FieldItem label="Razón Social">
                <Text style={{ color: token.colorText, lineHeight: 1.6 }}>
                  {company.name_company}
                </Text>
              </FieldItem>

              <FieldItem label="Régimen de Capital">
                <Text style={{ color: token.colorText }}>
                  {company.capital_regime}
                </Text>
              </FieldItem>
            </SectionCard>
          </div>

          {/* Información Comercial */}
          <div style={{ flex: "1 1 280px" }}>
            <SectionCard
              icon={<ShopOutlined />}
              title="Información Comercial"
              iconBg={token.colorSuccessBg}
              iconColor={token.colorSuccessText}
            >
              <FieldItem label="Nombre Comercial">
                <Text
                  strong
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: token.colorText,
                  }}
                >
                  {company.public_name_company}
                </Text>
              </FieldItem>

              <FieldItem label="Inicio de Operaciones">
                <Flex align="center" gap={6}>
                  <CalendarOutlined
                    style={{ color: token.colorTextSecondary }}
                  />
                  <Text style={{ color: token.colorText }}>
                    {formatDate(company.date_start_operations)}
                  </Text>
                </Flex>
              </FieldItem>

              <FieldItem label="Estatus SAT">
                <Flex align="center" gap={6}>
                  {satStatus.icon && (
                    <span
                      style={{
                        color:
                          satStatus.color === "success"
                            ? token.colorSuccess
                            : satStatus.color === "warning"
                              ? token.colorWarning
                              : token.colorError,
                        fontSize: 16,
                      }}
                    >
                      {satStatus.icon}
                    </span>
                  )}
                  <Text
                    style={{
                      fontWeight: 700,
                      color:
                        satStatus.color === "success"
                          ? token.colorSuccess
                          : satStatus.color === "warning"
                            ? token.colorWarning
                            : token.colorError,
                    }}
                  >
                    {satStatus.label}
                  </Text>
                </Flex>
              </FieldItem>
            </SectionCard>
          </div>
        </Flex>

        {/* ── Collapsible Detail Sections ── */}
        <Collapse
          items={collapseItems}
          style={{ borderRadius: token.borderRadiusLG }}
          defaultActiveKey={["actividades"]}
        />

        {/* ── Action ── */}
        <Button
          type="primary"
          size="large"
          icon={<EditOutlined />}
          onClick={() => navigate("/empresa/editar")}
          style={{
            height: 52,
            borderRadius: token.borderRadiusLG,
            fontWeight: 700,
            fontSize: 15,
            boxShadow: `0 8px 20px ${token.colorPrimaryBg}`,
          }}
          block
        >
          Editar Información
        </Button>
      </Flex>
    </>
  );
};

export default DetailsCompany;
